<template>
  <basic-dialog v-model="dialog" :actionButtons="actionButtons" :loading="saving" :title="$t('tasks.suggested_tasks')"
                display-buttons-as-blocks max-width="720">
    <template v-if="!loading">
      <v-card-text class="scroll-content">
        <div class="pt-6 pb-5">{{ $t('tasks.you_can_choose_suggested_task_or_create_own') }}</div>
        <div class="mx-n4">
          <v-list color="transparent" dense>
            <v-divider class="mb-4"></v-divider>
            <template v-for="taskGroupCategoryId in Object.keys(groupedTasks)">
              <suggested-tasks-list-item :key="`category_${taskGroupCategoryId}`" :category-id="taskGroupCategoryId"
                                         :selected-task-ids="selectedTaskIds"
                                         :tasks="groupedTasks[taskGroupCategoryId]"
                                         @category-select="handleCategorySelect" @task-select="handleTaskSelect"/>
            </template>
            <template v-if="tasksWithoutCategory.length > 0">
              <v-list-item @click="handleNoCategoriesClick">
                <v-list-item-icon>
                  <v-icon
                    v-if="allTasksWithoutCategorySelected"
                    color="primary">
                    mdi mdi-checkbox-marked
                  </v-icon>
                  <v-icon v-else-if="someTasksWithoutCategorySelected"
                          color="grey lighten-1">
                    mdi mdi-minus-box
                  </v-icon>
                  <v-icon v-else color="grey lighten-2">mdi mdi-checkbox-blank
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="subtitle-1" style="height: 20px; margin-top: 2px;">
                    {{ $t('tasks.no_category') }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item v-for="task in tasksWithoutCategory" :key="`task_${task.id}`"
                           :title="task.nameText" @click="handleTaskSelect(task)">
                <v-list-item-icon>
                  <v-icon v-if="selectedTaskIds.findIndex(stId => stId === task.id) > -1" color="primary">
                    mdi mdi-checkbox-outline
                  </v-icon>
                  <v-icon v-else>mdi mdi-checkbox-blank-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="subtitle-2">{{ task.nameText }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-list>
        </div>
      </v-card-text>
    </template>


    <v-card-text v-else class="py-10">
      <hb-loading-indicator align-middle/>
    </v-card-text>

    <template v-if="!$isPaidTenant" v-slot:append-button.own_task>
      <plan-chip class="ml-2"/>
    </template>
  </basic-dialog>
</template>

<script>
import BasicDialog from "@/components/BasicDialog";
import categoryApi from "@/api/category";
import SuggestedTasksListItem from "@/components/Tenant/Tasks/SuggestedTasksListItem";
import categoryService from "@/service/category.service";
import pLimit from 'p-limit';
import taskApi from "@/api/task";
import PlanChip from "@/components/Tenant/Reports/PlanChip";

export default {
  name: "SuggestedTasksDialog",
  components: {PlanChip, SuggestedTasksListItem, BasicDialog},
  props: ['suggestedTasks', 'votableTasks'],
  data() {
    return {
      dialog: false,
      loading: true,
      saving: false,
      selectedTaskIds: [],
    }
  },
  methods: {
    close() {
      this.dialog = false;
      this.saving = false;
      this.selectedTaskIds = [];
    },
    open() {
      this.dialog = true;
      this.saving = false;
      this.selectedTaskIds = [];

    },
    async selectSuggestedTasks() {
      this.saving = true;
      try {
        const limit = pLimit(3);
        const promises = [];
        const selectedSuggestedTasks = this.suggestedTasks.filter(task => this.selectedTaskIds.includes(task.id));
        for (let stask of selectedSuggestedTasks) {
          promises.push(limit(async () => {
            try {
              await taskApi.createTask({
                staticTaskId: stask.id,
                type: categoryService.getMainParentCategory(categoryService.getCategoryById(stask.categoryId))?.name,
                name: stask.nameText,
                description: stask.descriptionText,
                priority: stask.priority,
                category: categoryService.getCategoryById(stask.categoryId),
              })
            } catch (e) {
              this.$handleApiError(e, this.$t('errors.error_saving_task'));
            }
          }))
        }
        await Promise.all(promises);
        this.$showSuccessNotification(this.$t('tasks.suggested_tasks_saved'));
        if (!this.votableTasks || this.votableTasks.length === 0) {
          this.$emit('show-task-vote-help');
        }
      } catch (e) {
        console.error(e)
        this.$handleApiError(e, this.$t('errors.error_saving_task'));
      }
      this.saving = false;
      this.dialog = false;
      this.$emit('refresh');
      // save selected tasks
    },
    openOwnTaskDialog() {
      this.dialog = false;
      this.$emit('own-task');
    },
    async reloadCategories() {
      try {
        this.categories = await categoryApi.getCategories();
      } catch (e) {
        this.$handleApiError(e, this.$t('errors.error_loading_categories'));
      }
    },
    getCategoriesRecursively(categories, level = 0) {
      return categories.map(c => {
        c.level = level;
        c.children = this.getCategoriesRecursively(this.categories.filter(cc => cc.parentCategory?.id === c.id), level + 1);
        return c;
      });
    },
    handleCategorySelect(category, allTasksSelected, someTasksSelected) {
      if (allTasksSelected || someTasksSelected) {
        this.unselectTasksInCategory(category);
      } else {
        this.selectTasksInCategory(category);
      }
    },
    selectTasksInCategory(categoryId) {
      this.groupedTasks[categoryId].forEach(t => {
        if (this.selectedTaskIds.findIndex(stId => stId === t.id) === -1) {
          this.selectedTaskIds.push(t.id);
        }
      });
    },
    unselectTasksInCategory(categoryId) {
      this.groupedTasks[categoryId].forEach(t => {
        const taskIndex = this.selectedTaskIds.findIndex(stId => stId === t.id);
        if (taskIndex > -1) {
          this.selectedTaskIds.splice(taskIndex, 1);
        }
      });
    },
    handleTaskSelect(task) {
      const taskIndex = this.selectedTaskIds.findIndex(stId => stId === task.id);
      if (taskIndex > -1) {
        this.selectedTaskIds.splice(taskIndex, 1);
      } else {
        this.selectedTaskIds.push(task.id);
      }
    },
    handleNoCategoriesClick() {
      if (this.allTasksWithoutCategorySelected || this.someTasksWithoutCategorySelected) {
        this.unselectAllTasksWithoutCategory();
      } else {
        this.selectAllTasksWithoutCategory();
      }
    },
    selectAllTasksWithoutCategory() {
      this.tasksWithoutCategory.forEach(t => {
        if (this.selectedTaskIds.findIndex(stId => stId === t.id) === -1) {
          this.selectedTaskIds.push(t.id);
        }
      });
    },
    unselectAllTasksWithoutCategory() {
      this.tasksWithoutCategory.forEach(t => {
        const taskIndex = this.selectedTaskIds.findIndex(stId => stId === t.id);
        if (taskIndex > -1) {
          this.selectedTaskIds.splice(taskIndex, 1);
        }
      });
    },

  },
  computed: {

    actionButtons() {
      return {
        cancel: {
          click: this.close,
        },
        own_task: {
          click: this.openOwnTaskDialog,
          type: 'PRIMARY',
          icon: 'edit',
          text: this.$t('tasks.create_own_task'),
          disabled: !this.$isPaidTenant
        },
        static_task: {
          click: this.selectSuggestedTasks,
          type: 'PRIMARY',
          text: this.$t('tasks.select_suggested_tasks'),
          disabled: this.selectedTaskIds.length === 0
        },
      }
    },
    groupedTasks() {
      const tasks = this.suggestedTasks.reduce((acc, task) => {
        const taskCategory = categoryService.getCategoryById(task.categoryId)
        const mainCategory = categoryService.getMainParentCategory(taskCategory);
        if (!mainCategory) return acc;
        if (!acc[mainCategory.id]) {
          acc[mainCategory.id] = [];
        }
        acc[mainCategory.id].push(task);
        return acc;
      }, {});

      return tasks;
    },
    categories() {
      const categories = Object.keys(this.groupedTasks).map(id => {
        return categoryService.getCategoryById(parseInt(id));
      });
      return categories;
    },
    tasksWithoutCategory() {
      return this.suggestedTasks.filter(t => !t.categoryId);
    },
    allTasksWithoutCategorySelected() {
      return this.tasksWithoutCategory.length === this.tasksWithoutCategory.filter(ct => this.selectedTaskIds.includes(ct.id)).length;
    },
    someTasksWithoutCategorySelected() {
      return this.tasksWithoutCategory.filter(ct => this.selectedTaskIds.includes(ct.id)) > 0;
    }
  },
  async mounted() {
    this.loading = false;
  },
  watch: {
    selectedTaskIds() {
      if (this.selectedTaskIds.length > this.$planLimit.suggestedTasks) {
        this.selectedTaskIds = this.selectedTaskIds.slice(0, this.$planLimit.suggestedTasks);
        this.$showPlanNotification(this.$t('tasks.suggested_tasks_limit_reached', [this.$planLimit.suggestedTasks, this.$nextPlan.name]));
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.scroll-content {
  max-height: calc(100vh - 330px);
  overflow: auto;
}

@media screen and (min-width: 600px) {
  .scroll-content {
    max-height: calc(100vh - 350px);
    overflow: auto;
  }
}
</style>
